import { AtkSummaryCard } from "portal/components/atk/AtkSummaryCard";
import { DateTime } from "luxon";
import { DEFAULT_TIMEZONE } from "portal/utils/dates";
import { formatStatus, getColor, isOffline } from "portal/utils/robots";
import { portalApi } from "portal/state/portalApi";
import { RobotSummary } from "../../components/robots/RobotSummary";
import { RobotSummaryHeadlineInfo } from "portal/components/robots/RobotSummaryCard";
import { RobotSummaryResponse } from "protos/portal/robots";
import { RobotView } from "../robots/AnyRobotPage";
import { skipToken } from "@reduxjs/toolkit/query";
import { useJigsawMetrics } from "../../utils/hooks/useJigsawMetrics";
import { useParams } from "react-router-dom";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useRobotHistory } from "../robots/useRobotHistory";
import { useTranslation } from "react-i18next";
import PendingIcon from "@mui/icons-material/PendingOutlined";
import React, { FunctionComponent } from "react";
import StatusErrorIcon from "@mui/icons-material/ErrorOutlined";
import StatusOkIcon from "@mui/icons-material/CheckCircleOutlined";

interface AtkSummaryProps {
  offerViewSwitchButton?: RobotView;
}

export function useHeadlineInfo(
  summary: RobotSummaryResponse | undefined
): RobotSummaryHeadlineInfo {
  const { t, i18n } = useTranslation();
  const robotCropDisplayName = summary?.jigsawCrop?.localizedName;

  if (summary) {
    const color = getColor(summary);
    let StatusIcon;

    const checkin = summary.robot?.health?.reportedAt
      ? DateTime.fromSeconds(summary.robot.health.reportedAt, {
          zone: "utc",
        }).setZone(
          summary.robot.health.timezone === ""
            ? DEFAULT_TIMEZONE
            : summary.robot.health.timezone
        )
      : undefined;

    if (isOffline(summary.robot)) {
      StatusIcon = PendingIcon;
    } else if (color === "error") {
      StatusIcon = StatusErrorIcon;
    } else if (color === "warning") {
      StatusIcon = PendingIcon;
    } else {
      StatusIcon = StatusOkIcon;
    }

    return {
      StatusIcon,
      statusColor: color,
      headlineText: formatStatus(t, i18n, summary.robot, robotCropDisplayName),
      checkedIn: checkin,
    };
  } else {
    return {
      StatusIcon: PendingIcon,
      statusColor: "textDisabled",
      headlineText: "",
      checkedIn: undefined,
    };
  }
}

export const AtkSummary: FunctionComponent<AtkSummaryProps> = ({
  offerViewSwitchButton,
}) => {
  const { i18n } = useTranslation();
  const { serial } = useParams();
  const useJigsaw = useJigsawMetrics();

  const { data: summary, isSuccess } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken
    )
  );

  const history = useRobotHistory(summary?.robot?.serial);
  const { logs, hasBlocks, isLoading: isHistoryLoading } = history;
  const headlineInfo = useHeadlineInfo(summary);

  return (
    <RobotSummary
      success={isSuccess}
      serial={serial}
      customerSerial={summary?.robot?.customerSerial}
      headlineInfo={headlineInfo}
      mapInfo={{
        summary,
        isHistoryLoading,
        healthLogs: logs,
        blocks: history,
        hasBlocks: Boolean(hasBlocks),
      }}
      offerViewSwitchButton={offerViewSwitchButton}
      summaryCard={
        serial ? (
          <AtkSummaryCard serial={serial} summary={summary} variant="sidebar" />
        ) : (
          <></>
        )
      }
      machineHealthCards={[]}
      supportLinks={[]}
    />
  );
};
